import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Select, { SelectProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ReactNode, useEffect, useState } from "react";
import { css } from "styled-components";

interface ShortCoupleOpeningScreenProps {
    fields?: any;
}

interface FindShortCoupleOpeningStyles {

    wrapper?: InjectableCss;
    formWrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    searchButton?: ButtonPresentationProps;
}

export const findShortCoupleOpeningStyles: FindShortCoupleOpeningStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 24px;
            padding-bottom: 24px;
        `,
    },
    formWrapper: {
        css: css`
            width: 100%;
            max-width: 340px;
            display: flex;
            flex-direction: column;
            gap: 16px;
        `,
    },
    heading: {
        variant: "h2",
    },
    searchButton: {
        variant: "primary",
    },
};

const ShortCoupleOpeningScreen: React.FC<ShortCoupleOpeningScreenProps> = ({
    fields
}) => {
    const styles = findShortCoupleOpeningStyles;
    const [errorMessage, setErrorMessage] = useState<ReactNode>("");
    const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);
    const [clValue, setClValue] = useState("");
    const [selectedSeries, setSelectedSeries] = useState("");
    const [seriesData, setSeriesData] = useState<string[]>([]);

    useEffect(() => {
        const fetchSeries = async () => {
            try {
                const result = await fetch("/api/v1/findShortCoupleShaft/series");
                if (!result.ok) {
                    throw new Error(result.statusText);
                }
                const data = await result.json();
                setSeriesData(data);

            } catch (error) {
                console.error("Error fetching filters:", error);
            } finally {
                setShowLoadingOverlay(false);
            }
        };

        fetchSeries();
    }, []);

    const search = () => {
        if (!clValue || !selectedSeries) return;

        // Only allow valid integer or float numbers (no letters, no extra characters)
        if (!/^\d+(\.\d+)?$/.test(clValue.trim())) {
            setErrorMessage(translate("Please enter a valid number for C-L/C-L Installed."));
            return;
        }

        const params = new URLSearchParams({
            page_no: "1",
            series: selectedSeries,
            cl_data: clValue,
        });
        window.location.search = `?${params.toString()}`;
    }

    const handleSeriesChange: SelectProps["onChange"] = event => {
        setSelectedSeries(event.target.value);
    }

    return (
        <LoadingOverlay
            loading={showLoadingOverlay}
            css={css`
                        width: 100%;
                    `}
        >
            <StyledWrapper {...styles.wrapper} className="wrapper">
                <Typography {...styles.heading} as="h2">
                    {translate("Find Short Couple Shaft")}
                </Typography>
                <StyledWrapper {...styles.formWrapper} className="form-wrapper">
                    <Select
                        label={translate("Series")}
                        name="series"
                        onChange={handleSeriesChange}
                    >
                        <option value="">{translate("Select Series")}</option>
                        {seriesData && seriesData.map(s => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </Select>
                    <TextField
                        className="cl-data-text-field"
                        id="clDataTextField"
                        value={clValue}
                        onChange={e => {
                            setErrorMessage("");
                            setClValue(e.target.value);
                        }}
                        error={errorMessage}
                        label={translate("C-L/C-L Installed")}
                        hint={translate("Examples: 7.5 or 8 or 9.5")}
                    />
                    <Button {...styles.searchButton} onClick={search} disabled={!clValue || !selectedSeries}>
                        {translate("GO")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    )
};

export default ShortCoupleOpeningScreen;